import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Globe, KeyRound, Plus, Copy, RotateCcw, Trash2, Send, ScrollText, Loader2, Save, X,
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Clock, Pencil, Link2,
} from 'lucide-react';
import cauLacBoService from '../../services/cauLacBoService';
import clbKetNoiService from '../../services/clbKetNoiService';
import ConfirmDialog from '../common/ConfirmDialog';

const SCOPE_INFO = {
  'members:read': { label: 'Đọc danh sách thành viên', hint: 'Họ tên, MSSV, lớp, khoa, chức vụ' },
  'members:contact': { label: 'Thêm email + số điện thoại thành viên', hint: 'Chỉ cấp khi có thỏa thuận xử lý dữ liệu cá nhân', sensitive: true },
  'activities:read': { label: 'Đọc hoạt động của CLB', hint: 'Kể cả hoạt động chưa công khai' },
  'participation:read': { label: 'Đọc kết quả tham gia / điểm danh', hint: 'Sinh viên đã tham gia từng hoạt động' },
  'registrations:read': { label: 'Đọc đơn đăng ký thành viên', hint: 'Trạng thái chờ duyệt / đã duyệt / từ chối' },
};

const EVENT_INFO = {
  'member.joined': 'Thêm thành viên / duyệt đơn',
  'member.left': 'Thành viên rời CLB',
  'member.role_changed': 'Đổi chức vụ',
  'registration.approved': 'Duyệt đơn đăng ký',
  'registration.rejected': 'Từ chối đơn đăng ký',
  'club.updated': 'Cập nhật thông tin CLB',
};

const DELIVERY_STATUS = {
  SUCCESS: { label: 'Thành công', cls: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  PENDING: { label: 'Đang chờ gửi', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  FAILED: { label: 'Thất bại', cls: 'bg-red-100 text-red-700', icon: XCircle },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('vi-VN') : '—');
const errMsg = (e) => e?.response?.data?.message || e?.message || 'Có lỗi xảy ra';

function copy(text, label = 'Đã sao chép') {
  navigator.clipboard.writeText(text).then(() => toast.success(label), () => toast.error('Không sao chép được'));
}

// ── Hiển thị khóa MỘT LẦN ────────────────────────────────────────────────────
function SecretModal({ result, onClose }) {
  const rows = [
    result.apiKey && { label: 'Khóa API (X-Api-Key)', value: result.apiKey },
    result.webhookSecret && { label: 'Bí mật ký webhook', value: result.webhookSecret },
  ].filter(Boolean);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Sao chép ngay — chỉ hiển thị một lần</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Hệ thống chỉ lưu bản băm nên không thể xem lại. Nếu mất, hãy cấp khóa mới. Chỉ đặt trong cấu hình phía máy chủ
              của website, không đưa vào mã chạy trên trình duyệt hay đưa lên Git.
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.label}>
              <p className="text-xs font-medium text-gray-600 mb-1">{r.label}</p>
              <div className="flex gap-2">
                <input readOnly value={r.value} onFocus={(e) => e.target.select()}
                  className="flex-1 font-mono text-xs px-3 py-2 border rounded-lg bg-gray-50" />
                <button onClick={() => copy(r.value)} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1">
                  <Copy className="w-4 h-4" /> Chép
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={onClose} className="mt-5 w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
          Tôi đã lưu lại
        </button>
      </div>
    </div>
  );
}

// ── Tạo / sửa khóa ───────────────────────────────────────────────────────────
function ClientFormModal({ maClb, meta, client, onClose, onSaved }) {
  const editing = !!client;
  const [ten, setTen] = useState(client?.ten ?? '');
  const [scopes, setScopes] = useState(client?.scopes ?? ['members:read']);
  const [ip, setIp] = useState(client?.ipAllowlist ?? '');
  const [webhookUrl, setWebhookUrl] = useState(client?.webhookUrl ?? '');
  const [events, setEvents] = useState(client?.webhookEvents ?? []);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const mutation = useMutation({
    mutationFn: () => {
      const body = { ten, scopes, ipAllowlist: ip, webhookUrl, webhookEvents: events };
      return editing ? clbKetNoiService.update(maClb, client.id, body) : clbKetNoiService.create(maClb, body);
    },
    onSuccess: (res) => onSaved(res, editing),
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900">{editing ? 'Sửa kết nối' : 'Tạo khóa kết nối website'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700">Tên website *</label>
            <input value={ten} onChange={(e) => setTen(e.target.value)} placeholder="VD: Website CLB Lập trình"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Quyền của khóa (chỉ cấp phần cần dùng)</p>
            <div className="space-y-2">
              {(meta?.scopes ?? Object.keys(SCOPE_INFO)).map((s) => {
                const info = SCOPE_INFO[s] ?? { label: s };
                return (
                  <label key={s} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer ${scopes.includes(s) ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                    <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(scopes, setScopes, s)} className="mt-1" />
                    <span className="text-sm">
                      <span className="font-medium">{info.label}</span>
                      {info.sensitive && <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">NHẠY CẢM</span>}
                      <span className="block text-xs text-gray-500">{info.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Chỉ cho phép các IP (tùy chọn)</label>
            <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="VD: 203.0.113.10, 198.51.100.0/24"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono" />
            <p className="text-xs text-gray-500 mt-1">IP máy chủ của website. Để trống = không giới hạn IP. Hỗ trợ dải CIDR.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Địa chỉ nhận webhook (tùy chọn)</label>
            <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://web-clb.example/hook/tuoitre"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono" />
            <p className="text-xs text-gray-500 mt-1">Phải là https và địa chỉ công khai. Hệ thống sẽ báo khi có thay đổi thành viên, đơn đăng ký…</p>
            {webhookUrl.trim() && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {(meta?.events ?? Object.keys(EVENT_INFO)).map((ev) => (
                  <label key={ev} className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={events.includes(ev)} onChange={() => toggle(events, setEvents, ev)} />
                    <span><span className="font-mono">{ev}</span> — {EVENT_INFO[ev]}</span>
                  </label>
                ))}
                <p className="sm:col-span-2 text-xs text-gray-500">Không chọn sự kiện nào = nhận tất cả.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-white">Hủy</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !ten.trim() || scopes.length === 0}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editing ? 'Lưu' : 'Tạo khóa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Nhật ký webhook ──────────────────────────────────────────────────────────
function WebhookLogsModal({ maClb, client, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['clb-webhook-logs', maClb, client.id],
    queryFn: () => clbKetNoiService.webhookLogs(maClb, client.id, 0, 30),
  });
  const retry = useMutation({
    mutationFn: (id) => clbKetNoiService.guiLai(maClb, client.id, id),
    onSuccess: () => { toast.success('Đã đặt lại, hệ thống sẽ gửi trong ít giây'); qc.invalidateQueries({ queryKey: ['clb-webhook-logs', maClb, client.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rows = data?.content ?? [];
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-gray-900">Nhật ký webhook — {client.ten}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} className="p-1.5 rounded hover:bg-gray-100" title="Làm mới"><RotateCcw className="w-4 h-4" /></button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="overflow-auto p-4">
          {isLoading ? <p className="text-center text-sm text-gray-500 py-8">Đang tải…</p>
            : rows.length === 0 ? <p className="text-center text-sm text-gray-500 py-8">Chưa có lần gửi nào.</p>
              : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-2">Thời gian</th><th className="pr-2">Sự kiện</th><th className="pr-2">Trạng thái</th>
                    <th className="pr-2">Lần</th><th>Chi tiết</th><th />
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => {
                      const st = DELIVERY_STATUS[r.status] ?? DELIVERY_STATUS.PENDING;
                      return (
                        <tr key={r.id} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-2 whitespace-nowrap text-xs">{fmt(r.createdAt)}</td>
                          <td className="pr-2 font-mono text-xs">{r.event}</td>
                          <td className="pr-2"><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${st.cls}`}><st.icon className="w-3 h-3" />{st.label}</span></td>
                          <td className="pr-2 text-xs">{r.attempts}</td>
                          <td className="text-xs text-gray-600">
                            {r.lastStatusCode ? `HTTP ${r.lastStatusCode}` : ''} {r.lastError || ''}
                            {r.status === 'PENDING' && r.nextRetryAt && <span className="block text-gray-400">Thử lại: {fmt(r.nextRetryAt)}</span>}
                          </td>
                          <td className="text-right">
                            {r.status === 'FAILED' && (
                              <button onClick={() => retry.mutate(r.id)} disabled={retry.isPending}
                                className="text-xs px-2 py-1 border rounded hover:bg-gray-50">Gửi lại</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

// ── Tab chính ────────────────────────────────────────────────────────────────
export default function ClbKetNoiTab({ clb }) {
  const qc = useQueryClient();
  const maClb = clb.maClb;
  const [form, setForm] = useState(null);          // null | { client? }
  const [secret, setSecret] = useState(null);      // kết quả chứa khóa bản rõ
  const [logsFor, setLogsFor] = useState(null);
  const [confirm, setConfirm] = useState(null);    // { type: 'revoke'|'rotate', client }
  const [website, setWebsite] = useState(clb.websiteUrl ?? '');

  const { data: meta } = useQuery({ queryKey: ['clb-ket-noi-meta', maClb], queryFn: () => clbKetNoiService.meta(maClb), staleTime: 10 * 60 * 1000 });
  const { data: clients = [], isLoading } = useQuery({ queryKey: ['clb-ket-noi', maClb], queryFn: () => clbKetNoiService.list(maClb) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['clb-ket-noi', maClb] });

  const saveWebsite = useMutation({
    // PUT /api/clb/{ma} ghi đè mọi trường → gửi lại đầy đủ bản hiện có kèm websiteUrl mới
    mutationFn: () => cauLacBoService.update(maClb, { ...clb, websiteUrl: website.trim() }),
    onSuccess: () => { toast.success('Đã lưu địa chỉ website'); qc.invalidateQueries({ queryKey: ['clb'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const action = useMutation({
    mutationFn: async ({ type, client }) => {
      if (type === 'revoke') { await clbKetNoiService.thuHoi(maClb, client.id); return null; }
      if (type === 'rotate') return clbKetNoiService.xoayKhoa(maClb, client.id);
      if (type === 'rotateSecret') return clbKetNoiService.xoayWebhookSecret(maClb, client.id);
      if (type === 'ping') return clbKetNoiService.kiemTraWebhook(maClb, client.id);
      return null;
    },
    onSuccess: (res, { type }) => {
      setConfirm(null);
      if (type === 'revoke') toast.success('Đã thu hồi khóa');
      if (type === 'rotate' || type === 'rotateSecret') setSecret(res);
      if (type === 'ping') {
        if (res.status === 'SUCCESS') toast.success(`Webhook phản hồi HTTP ${res.lastStatusCode} — kết nối tốt`);
        else toast.error(`Webhook lỗi: ${res.lastError || 'không rõ'}`);
      }
      refresh();
    },
    onError: (e) => { setConfirm(null); toast.error(errMsg(e)); },
  });

  const onSaved = (res, editing) => {
    setForm(null);
    refresh();
    if (editing) toast.success('Đã lưu');
    else setSecret(res);
  };

  const active = clients.filter((c) => c.isActive);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
        <Link2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          Kết nối <b>website riêng của CLB</b> với Tuổi Trẻ KGU để tự động lấy danh sách thành viên, hoạt động… và nhận thông báo
          khi có thay đổi. Dữ liệu luôn lấy từ Tuổi Trẻ nên khớp với điểm rèn luyện của trường. Mỗi khóa chỉ truy cập được
          <b> CLB này</b> và đúng phần quyền bạn cấp.
        </p>
      </div>

      {/* Website riêng */}
      <section className="border rounded-xl p-4">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-1"><Globe className="w-4 h-4" /> Website riêng của CLB</h4>
        <p className="text-xs text-gray-500 mb-3">Hiển thị nút “Đến website CLB” ở trang tổng hợp, nối các website thành một chuỗi.</p>
        <div className="flex gap-2">
          <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://clb-lap-trinh.example"
            className="flex-1 px-3 py-2 border rounded-lg text-sm" />
          <button onClick={() => saveWebsite.mutate()} disabled={saveWebsite.isPending || website.trim() === (clb.websiteUrl ?? '')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saveWebsite.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Lưu
          </button>
        </div>
      </section>

      {/* Danh sách khóa */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Khóa kết nối ({active.length} đang hoạt động)</h4>
          <button onClick={() => setForm({})} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Tạo khóa
          </button>
        </div>

        {isLoading ? <p className="text-sm text-gray-500 py-6 text-center">Đang tải…</p>
          : clients.length === 0 ? (
            <div className="text-center py-10 border border-dashed rounded-xl text-sm text-gray-500">
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              Chưa có khóa nào. Bấm “Tạo khóa” để cấp cho website của CLB.
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map((c) => (
                <div key={c.id} className={`border rounded-xl p-4 ${c.isActive ? 'bg-white' : 'bg-gray-50 opacity-70'}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{c.ten}</span>
                        {c.revokedAt
                          ? <span className="text-[11px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Đã thu hồi</span>
                          : c.isActive
                            ? <span className="text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Đang hoạt động</span>
                            : <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Tạm tắt</span>}
                      </div>
                      <p className="font-mono text-xs text-gray-500 mt-1">{c.keyPrefix}…</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.scopes.map((s) => (
                          <span key={s} className={`text-[11px] px-2 py-0.5 rounded ${s === 'members:contact' ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{s}</span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Dùng lần cuối: {fmt(c.lastUsedAt)}{c.lastUsedIp ? ` (IP ${c.lastUsedIp})` : ''}
                        {c.ipAllowlist ? ` · Giới hạn IP: ${c.ipAllowlist}` : ''}
                      </p>
                      {c.webhookUrl && <p className="text-xs text-gray-500 mt-0.5 break-all">Webhook: {c.webhookUrl}</p>}
                    </div>
                    {c.isActive && (
                      <div className="flex flex-wrap gap-1.5">
                        <button onClick={() => setForm({ client: c })} className="px-2.5 py-1.5 border rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />Sửa</button>
                        <button onClick={() => setConfirm({ type: 'rotate', client: c })} className="px-2.5 py-1.5 border rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />Cấp khóa mới</button>
                        {c.webhookUrl && (
                          <>
                            <button onClick={() => action.mutate({ type: 'ping', client: c })} disabled={action.isPending} className="px-2.5 py-1.5 border rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1"><Send className="w-3.5 h-3.5" />Gửi thử</button>
                            <button onClick={() => setLogsFor(c)} className="px-2.5 py-1.5 border rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1"><ScrollText className="w-3.5 h-3.5" />Nhật ký</button>
                            <button onClick={() => setConfirm({ type: 'rotateSecret', client: c })} className="px-2.5 py-1.5 border rounded-lg text-xs hover:bg-gray-50">Đổi bí mật webhook</button>
                          </>
                        )}
                        <button onClick={() => setConfirm({ type: 'revoke', client: c })} className="px-2.5 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-50 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Thu hồi</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>

      {/* Hướng dẫn nhanh cho nhóm làm website */}
      <section className="border rounded-xl p-4 bg-gray-50">
        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Hướng dẫn cho nhóm làm website</h4>
        <p className="text-xs text-gray-600 mb-2">Gọi từ <b>máy chủ</b> của website (không gọi từ trình duyệt), kèm header <span className="font-mono">X-Api-Key</span>:</p>
        <pre className="text-xs bg-white border rounded-lg p-3 overflow-x-auto">{`curl -H "X-Api-Key: <khóa>" \\
  "${window.location.origin}/api/partner/v1/clb/${maClb}/members?size=50"`}</pre>
        <p className="text-xs text-gray-500 mt-2">Tài liệu đầy đủ: <span className="font-mono">CLB_WEBSITE_INTEGRATION.md</span> trong kho mã nguồn.</p>
      </section>

      {form && <ClientFormModal maClb={maClb} meta={meta} client={form.client} onClose={() => setForm(null)} onSaved={onSaved} />}
      {secret && <SecretModal result={secret} onClose={() => setSecret(null)} />}
      {logsFor && <WebhookLogsModal maClb={maClb} client={logsFor} onClose={() => setLogsFor(null)} />}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => action.mutate(confirm)}
        isLoading={action.isPending}
        variant={confirm?.type === 'revoke' ? 'danger' : 'warning'}
        title={confirm?.type === 'revoke' ? 'Thu hồi khóa?' : confirm?.type === 'rotate' ? 'Cấp khóa mới?' : 'Đổi bí mật webhook?'}
        description={
          confirm?.type === 'revoke' ? `Website “${confirm?.client?.ten}” sẽ không truy cập được nữa ngay lập tức. Không thể khôi phục.`
            : confirm?.type === 'rotate' ? `Khóa cũ của “${confirm?.client?.ten}” sẽ hết hiệu lực NGAY. Bạn phải cập nhật khóa mới vào website.`
              : `Website phải cập nhật bí mật mới để xác minh chữ ký webhook, nếu không sẽ từ chối các thông báo.`
        }
        confirmLabel={confirm?.type === 'revoke' ? 'Thu hồi' : 'Tiếp tục'}
      />
    </div>
  );
}
